import { useCodeListStore } from '@/app/shared/state/CodeListModule';
import { Mri } from '@/app/mri/shared/services/services';
import {isEmpty, isEqual} from 'lodash-es';
import {computed, defineComponent, onMounted, Ref, ref, useTemplateRef, watch} from 'vue';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import IpproAutocomplete from '@/app/shared/components/ippro-autocomplete/ippro-autocomplete.vue';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import * as yup from 'yup';

const notAVatNumber = (value: string) => {
    if (!value) {
        return true;
    }

    const normalizedValue = value.replace(/[\s.]/g, '');
    // Regex for Belgian VAT: BE0 followed by 9 digits OR 0 followed by 9 digits
    // We check if the input contains this pattern anywhere
    const vatPattern = /((BE)?0\d{9})/i;
    return !vatPattern.test(normalizedValue);
};

export const createAddressSchema = () => yup.object({
    street: yup.string().required().max(200).label('Straat').test('not-a-vat-number', 'De straatnaam mag geen btw-nummer bevatten.', notAVatNumber),
    streetNumber: yup.string().required().max(50).label('Nummer'),
    box: yup.string().notRequired().max(10).label('Bus'),
    zip: yup.string().required().max(10).label('Postcode'),
    city: yup.string().required().max(200).label('Gemeente').test('not-a-vat-number', 'De gemeente mag geen btw-nummer bevatten.', notAVatNumber),
    countryCode: yup.string().required().label('Land').default('BE'),
});

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlAddressForm',
    components: {IpproAutocomplete},
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: Object as () => Mri.IAddress,
            required: true,
        },
        formPath: { type: String, required: true },
    },
    setup(props, { emit }) {

        const schema = createAddressSchema();

        const streetMinLength: number = 2;

        const streetNumberRef = useTemplateRef<InstanceType<typeof IpproAutocomplete>>('streetNumberRef');

        const streetData = ref<AutoCompleteResult[]>([]);
        const streetDataFetching = ref<boolean>(false);
        const streetNumberData = ref<AutoCompleteResult[]>([]);
        const streetNumberDataFetching = ref<boolean>(false);


        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema, props.formPath);

        const street = fields.street.getValue<string>();
        const streetNumber = fields.streetNumber.getValue<string>();
        const box = fields.box.getValue<string>();
        const zip = fields.zip.getValue<string>();
        const city = fields.city.getValue<string>();
        const countryCode = fields.countryCode.getValue<string>();

        const address = computed<Mri.IAddress>(() => ({
            box: box.value,
            city: city.value,
            countryCode: countryCode.value,
            street: street.value,
            streetNumber: streetNumber.value,
            zip: zip.value,
        }));

        const autocompleteActive = computed(() => {
            return countryCode.value === 'BE';
        });

        const countries = computed(() => {
            return useCodeListStore().countryCodes;
        });


        const onStreetInputChange = (newValue: string) => {
            street.value = newValue;
            if (autocompleteActive.value && newValue?.length > streetMinLength) {
                _fetchStreet(newValue);
            }
        }

        const onStreetNumberInputChange = (newValue: string) => {
            streetNumber.value = newValue;
            if (autocompleteActive.value && street.value && city.value) {
                _fetchStreetNumbers(street.value, city.value);
            }
        }

        const onStreetSelect = (streetSuggestion: string) => {
            const newAddress = _explodedAddressString(streetSuggestion);
            if (!newAddress.street) {
                return;
            }
            street.value = newAddress.street;
            if (newAddress.streetNumber) {
                streetNumber.value = newAddress.streetNumber;
            } else {
                if (!streetNumber.value) {
                    _focusToStreetNumber();
                }
            }
            if (newAddress.box) { box.value = newAddress.box; }
            if (newAddress.zip) {
                zip.value = newAddress.zip;
            } else {
                if (newAddress.city && newAddress.street) {
                    _fetchZipCodes(newAddress.street, newAddress.city);
                }
            }
            if (newAddress.city) {
                city.value = newAddress.city;
            }
        }

        const _focusToStreetNumber = () => {
            streetNumberRef.value.focus();
        }

        const onStreetNumberSelect = (streetNumberSuggestion: string) => {
            streetNumber.value = streetNumberSuggestion;
            if (!isEmpty(streetNumber.value)) {
                const parts = streetNumber.value.match(/[a-z]+|[^a-z]+/gi);
                if (parts[0]) {
                    streetNumber.value = parts[0];
                }
                if (parts[1]) {
                    box.value = parts[1];
                }
            }
        }

        const _fetchStreet = (street: string) => {
            streetDataFetching.value = true;
            useKlipApiProxy().location_GetAddressSuggestion(street)
                .then((response) => {
                    streetData.value = response.result
                        .map((item): AutoCompleteResult => {
                            const parts = item.split(',');
                            return {
                                title: parts[0],
                                subtitle: parts[1],
                                value: item,
                            };
                        });
                }).finally(() => {
                streetDataFetching.value = false;
            });
        }

        const _fetchStreetNumbers = (street: string, city: string) => {
            if (street && city) {
                streetNumberDataFetching.value = true;
                useKlipApiProxy().location_GetStreetNumbers(street, city)
                    .then((response) => {
                        if (isEmpty(response.result)) {
                            return;
                        }
                        streetNumberData.value = response.result
                            .filter((currentNr: string) => {
                                if (isEmpty(currentNr)) {
                                    return false;
                                }
                                if (isEmpty(streetNumber.value)) {
                                    return true;
                                }
                                return currentNr.startsWith(streetNumber.value);
                            })
                            .map((currentNr: string): AutoCompleteResult => {
                                return {
                                    title: currentNr,
                                    value: currentNr,
                                };
                            });
                    }).finally(() => {
                    streetNumberDataFetching.value = false;
                });
            }
        }

        const _fetchZipCodes = (street: string, city: string) => {
            useKlipApiProxy().location_GetZipCodes(street, city)
                .then((response) => {
                    if (response.result.length !== 0) {
                        zip.value = response.result[0];
                    }
                });
        }

        const _explodedAddressString = (address: string) => {
            const explodedAddress: Mri.IAddress = {};
            if (address.length) {
                if (address.includes(', ')) {
                    const addressParts = address.split(', ');
                    const streetDetails = addressParts[0];
                    const cityDetails = addressParts[1];
                    if (streetDetails.includes(' ')) {
                        const lastSpace = streetDetails.lastIndexOf(' ');
                        const firstPart = streetDetails.substr(0, lastSpace);
                        const secondPart = streetDetails.substr(lastSpace + 1);
                        explodedAddress.street = streetDetails;
                        if (parseInt(secondPart, 10)) {
                            explodedAddress.street = firstPart;
                            explodedAddress.streetNumber = secondPart;
                        }
                    }
                    if (!explodedAddress.street) { explodedAddress.street = streetDetails; }

                    if (cityDetails.includes(' ')) {
                        const cityParts = cityDetails.split(' ');
                        // There are some cities with spaces in the name (ex: De Haan, De Pinte), se we need to check if the possible zipcode is a number.
                        const isInvalidZipCode = isNaN(+cityParts[0]);
                        explodedAddress.zip = isInvalidZipCode ? '' : cityParts[0];
                        explodedAddress.city = isInvalidZipCode ? cityDetails : cityParts[1];
                    } else {
                        explodedAddress.city = cityDetails;
                    }
                } else {
                    explodedAddress.street = address;
                }
            }
            return explodedAddress;
        }

        watch(
            address,
            (newValue: Mri.IAddress, oldValue: Mri.IAddress) => {
                if (!isEqual(newValue, oldValue)) {
                    emit('update:modelValue', newValue);
                }
            },
            {immediate: false, deep: true});

        watch(
            autocompleteActive,
            (val: boolean, oldVal: boolean) => {
                if (!val) {
                    streetData.value = [];
                    streetNumberData.value = [];
                }
            },
            { immediate: false, deep: false })


        onMounted(() => {
            // _setAddress(props.modelValue);
        });

        return {
            streetData,
            streetDataFetching,
            streetNumberData,
            streetNumberDataFetching,
            autocompleteActive,
            countries,

            onStreetInputChange,
            onStreetNumberInputChange,
            onStreetSelect,
            onStreetNumberSelect,

            street,
            streetNumber,
            box,
            zip,
            city,
            countryCode,

            fields,
        }
    }
})
